import { createSlice } from "@reduxjs/toolkit";
import { addLeadSource, addLeadStatus, createLead, fetchLeadPurpose, fetchLeads, fetchLeadSource, fetchLeadStatus, updateLeadStatus } from "./leadThunk";

const getSavedLeadFilters = () => {
  try {
    const saved = sessionStorage.getItem("crmLeadFilters");
    return saved
      ? JSON.parse(saved)
      : {
          search: "",
          source: null,
          status: null,
          purpose: null,
          assigned_to: null,
          sort_by: "created_desc",
        };
  } catch {
    return {
      search: "",
      source: null,
      status: null,
      purpose: null,
      assigned_to: null,
      sort_by: "created_desc",
    };
  }
};

const DEFAULT_VISIBLE_COLUMNS = [
  "no",
  "name",
  "phone",
  "assigned_to",
  "status",
  "last_updated",
  "actions",
];

  const getSavedVisibleColumns = () => {
    try {
      const saved = sessionStorage.getItem("crmLeadVisibleColumns");
      return saved ? JSON.parse(saved) : DEFAULT_VISIBLE_COLUMNS;
    } catch {
      return DEFAULT_VISIBLE_COLUMNS;
    }
  };

const initialState = {
  list: [],
  loading: false,
  createLoading: false,
  filters: {
    status: "ALL",
  },
  statusList: [],
  sourceList: [],
  purposeList: [],
  filterState: getSavedLeadFilters(),
  visibleColumns: getSavedVisibleColumns(),
};

const leadSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };      
    },

    setLeadFilterState: (state, action) => {
      state.filterState = {
        ...state.filterState,
        ...action.payload,
      };
      sessionStorage.setItem(
        "crmLeadFilters",
        JSON.stringify(state.filterState)
      );
    },
    resetLeadFilterState: (state) => {
      state.filterState = {
        search: "",
        source: null,
        status: null,
        purpose: null,
        assigned_to: null,
        date_from: null,
        date_to: null,
        sort_by: "created_desc",
      };

      sessionStorage.removeItem("crmLeadFilters");
    },

    // Only for a genuinely fresh visit to the Leads page (PUSH navigation,
    // e.g. the sidebar or another module) -- NOT when the user is popping
    // back here from a lead's own detail page (POP navigation), where the
    // search is meant to still be showing. See the navigationType check in
    // index.js, the only caller.
    resetLeadSearchFilter: (state) => {
      if (!state.filterState.search) return; // no-op: keeps the same reference

      state.filterState = {
        ...state.filterState,
        search: "",
      };

      sessionStorage.setItem(
        "crmLeadFilters",
        JSON.stringify(state.filterState)
      );
    },

    setVisibleColumns: (state, action) => {
        state.visibleColumns = action.payload;
        sessionStorage.setItem(
            "crmLeadVisibleColumns",
            JSON.stringify(action.payload)
        );
    },

    resetVisibleColumns: (state) => {
        state.visibleColumns = DEFAULT_VISIBLE_COLUMNS;
        sessionStorage.removeItem("crmLeadVisibleColumns");
    },

  },
  
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchLeads.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        const { id, action: act } = action.payload;

        const lead = state.list.find((l) => l.id === id);
        if (lead) {
            if (act === "convert") {
              lead.status = {
                label: "Converted",
                color: "green",
              };
            }

            if (act === "reject") {
              lead.status = {
                label: "Lost",
                color: "red",
              };
            }
        }
     })
     .addCase("leads/updateLocalStatus", (state, action) => {
      const { id, status_id } = action.payload;
      const lead = state.list.find(l => l.id === id);
      if (lead) {
        lead.status_id = status_id;
      }
    })
     .addCase(createLead.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(createLead.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(createLead.rejected, (state) => {
        state.createLoading = false;
      })
      .addCase(fetchLeadStatus.fulfilled, (state, action) => {
        state.statusList = action.payload;
      })
      .addCase(fetchLeadSource.fulfilled, (state, action) => {
        state.sourceList = action.payload;
      })
      .addCase(addLeadStatus.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(addLeadStatus.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(addLeadStatus.rejected, (state) => {
        state.createLoading = false;
      })
      .addCase(addLeadSource.pending, (state) => {
        state.createLoading = true;
      })
      .addCase(addLeadSource.fulfilled, (state) => {
        state.createLoading = false;
      })
      .addCase(addLeadSource.rejected, (state) => {
        state.createLoading = false;
      })
      .addCase(fetchLeadPurpose.fulfilled, (state, action) => {
        state.purposeList = action.payload;
      });
  },
});

export const { setFilters, setLeadFilterState, resetLeadFilterState, resetLeadSearchFilter, setVisibleColumns, resetVisibleColumns } = leadSlice.actions;
export default leadSlice.reducer;